import {
  useCallback, useEffect, useId, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode, type Ref,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Ellipsis, History, Inbox, Newspaper, Settings2, SquarePen, X } from 'lucide-react';
import { assistantPageLabel, type AssistantConversationDTO, type AssistantObjectRef } from '@contracts/assistant';
import type { Id } from '@contracts/common';
import type { IngestJobDTO } from '@contracts/ingest';
import { formatDateTime } from '../../components/DateField';
import { MODAL_OPEN } from '../../components/layout/app-shell';
import { ModalDialog } from '../../components/ModalDialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useNarrowView } from '../../lib/media';
import { confirmLeave } from '../assistant-admin/useLeaveGuard';
import { WORKPLACE_VIEW_LABEL, workplaceViewOf } from '../workplace/model';
import { cn } from '../../lib/utils';
import { PANEL_ID, useAssistant } from './AssistantProvider';
import { conversationKey, dropPending, useConversation, useConversations, useOwnAppearance } from './api';
import { Composer } from './Composer';
import { Hana } from './Hana';
import { pendingBatchesKey } from './ingest/api';
import { PendingBatches, usePendingCount } from './ingest/PendingBatches';
import { useIngestInput } from './ingest/useIngestInput';
import { useMemoryNotices } from './memory/api';
import { RememberLine } from './memory/RememberLine';
import { MessageList } from './MessageList';
import { refKey, useResolved } from './ObjectChip';
import { usePageContext } from './page-context';
import { ASSISTANT_ME_PATH } from './me/tabs';
import { receivesBriefings, useProactivePrefs } from './proactive/api';
import { BriefingHistory } from './proactive/BriefingHistory';
import { NudgeTray } from './proactive/NudgeTray';
import { refreshFor } from './refresh';
import { useExtConfirm } from './useExtConfirm';
import { useTurn } from './useTurn';

const ICON_BUTTON = 'flex size-11 items-center justify-center rounded-lg text-[hsl(var(--h-muted-foreground))] '
  + 'transition-colors hover:bg-[hsl(var(--h-muted))] hover:text-[hsl(var(--h-card-foreground))] lg:size-7';

const PRESSED = 'bg-[hsl(var(--h-primary)/0.1)] text-[hsl(var(--h-primary))]';

function HeadButton({ label, onClick, pressed, children, ref }: {
  label: string; onClick: () => void; pressed?: boolean; children: ReactNode; ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <button ref={ref} type="button" aria-label={label} title={label} aria-pressed={pressed} onClick={onClick}
      className={cn(ICON_BUTTON, pressed === true && PRESSED)}>
      {children}
    </button>
  );
}

/** A menu item that shows a panel view: the view on screen is tinted, never a left bar. */
const ITEM = 'gap-2';
const ITEM_ON = 'bg-[hsl(var(--h-primary)/0.1)] text-[hsl(var(--h-primary))] focus:bg-[hsl(var(--h-primary)/0.14)] focus:text-[hsl(var(--h-primary))]';

type PanelView = 'chat' | 'history' | 'briefings' | 'batches';
/** "Mở" on a pending batch (D120): the batch, its conversation, and how fresh that conversation was when it was asked. */
interface Jump { jobId: Id; conversationId: Id; since: number }

/**
 * D120/P14: once the batch's conversation is on screen and read again, its card is scrolled to the top of the log and
 * takes the focus; a card the loaded messages do not hold (older than the page the server sends, or gone) opens the
 * batch page instead. A card still loading its batch is waited for, so the focus lands on the card itself.
 */
function useJumpToCard(jump: Jump | null, done: () => void, fallback: (jobId: Id) => void) {
  const { conversationId } = useAssistant();
  const conversation = useConversation(jump?.conversationId ?? null);
  const { dataUpdatedAt, errorUpdatedAt, isFetching } = conversation;
  useEffect(() => {
    if (jump === null || conversationId !== jump.conversationId) return undefined;
    const find = () => document.querySelector<HTMLElement>(
      `[data-assistant-panel] [data-batch-card="${CSS.escape(jump.jobId)}"]`);
    const land = (el: HTMLElement) => {
      // An instant scroll, then the log is told at once, so its stick-to-bottom lets the card stay where it is.
      el.scrollIntoView({ block: 'start', behavior: 'auto' });
      el.closest('[role="log"]')?.dispatchEvent(new Event('scroll'));
      el.focus({ preventScroll: true });
      done();
    };
    const el = find();
    if (el !== null && el.getAttribute('aria-busy') !== 'true') {
      land(el);
      return undefined;
    }
    if (el !== null) {
      // The card is there, its batch still on its way: look again until it is drawn (a few seconds at most).
      let tries = 0;
      const timer = window.setInterval(() => {
        const now = find();
        tries += 1;
        if (now === null) {
          // The card went (its batch is gone, or failed to read): stop looking once the few seconds are up.
          if (tries >= 50) {
            window.clearInterval(timer);
            done();
          }
          return;
        }
        if (now.getAttribute('aria-busy') !== 'true' || tries >= 50) {
          window.clearInterval(timer);
          land(now);
        }
      }, 100);
      return () => window.clearInterval(timer);
    }
    // Not in the messages: only an answer read after "Mở" settles that it is not there.
    if (!isFetching && (dataUpdatedAt > jump.since || errorUpdatedAt > jump.since)) {
      done();
      fallback(jump.jobId);
    }
    return undefined;
  }, [jump, conversationId, dataUpdatedAt, errorUpdatedAt, isFetching, done, fallback]);
}

/** Only a drag that carries files is the panel's (§7.1): text, a link or a Map card being moved is never taken. */
const carriesFiles = (e: DragEvent<HTMLElement>): boolean => Array.from(e.dataTransfer.types).includes('Files');

/**
 * Drop files on the panel to have them read (§7.1, mockup §1 `.drop`). The handlers sit on `[data-assistant-panel]`
 * itself, so a drag over the page (the Map's own drop) never reaches them; `enabled` is false where ingest is not
 * usable, and then nothing is prevented at all.
 */
function useDropZone(enabled: boolean, onFiles: (files: File[]) => void) {
  const [over, setOver] = useState(false);
  const handlers = enabled ? {
    onDragOver: (e: DragEvent<HTMLElement>) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setOver(true);
    },
    onDragLeave: (e: DragEvent<HTMLElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
    },
    onDrop: (e: DragEvent<HTMLElement>) => {
      setOver(false);
      if (!carriesFiles(e)) return;
      e.preventDefault();
      onFiles(Array.from(e.dataTransfer.files));
    },
  } : {};
  return { over: enabled && over, handlers };
}

/** What the status line says Hana is looking at: the page, and on the Workplace the view its URL names (P2 review F19). */
function pageStatusLabel(pathname: string, search: string): string | null {
  const page = assistantPageLabel(pathname);
  const view = pathname === '/workplace' ? workplaceViewOf(search) : null;
  return page !== null && view !== null ? `${page} · ${WORKPLACE_VIEW_LABEL[view]}` : page;
}

const NO_REFS: readonly AssistantObjectRef[] = [];

/**
 * D208, the workplace mockup's `.ctx`: "📌 Đang chọn: …" while the page has an object (the card picked on the board, the
 * project or partner open). The name is the server's (§6.2), so an object this person cannot see names nothing and
 * the line stays away; so it does while the name is on its way.
 */
function SelectedLine({ objectRef }: { objectRef: AssistantObjectRef | undefined }) {
  const refs = useMemo(() => (objectRef === undefined ? NO_REFS : [objectRef]), [objectRef]);
  const resolved = useResolved(refs);
  const found = objectRef === undefined ? undefined : resolved.get(refKey(objectRef));
  const label = found === undefined || found === 'failed' ? null : found.label;
  if (label === null) return null;
  return (
    <p data-selected-object="" title={`Đang chọn: ${label}`}
      className="mx-3 mt-2 truncate rounded-md border border-[hsl(var(--h-icon-bg-violet))] bg-[hsl(var(--h-tone-violet-tint))] px-2 py-1 text-xs text-[hsl(var(--h-icon-violet))]">
      <span aria-hidden="true">📌 </span>Đang chọn: <b className="font-semibold">{label}</b>
    </p>
  );
}

/** The person's own conversations, newest first; the one on screen is tinted (never a left bar). */
function HistoryList({ onPick }: { onPick: (id: string) => void }) {
  const { conversationId } = useAssistant();
  const { data, isPending } = useConversations(true);
  if (isPending) return <p className="px-3 py-3 text-sm text-[hsl(var(--h-muted-foreground))]">Đang tải…</p>;
  if (data === undefined || data.length === 0) {
    return <p className="px-3 py-3 text-sm text-[hsl(var(--h-muted-foreground))]">Chưa có hội thoại nào.</p>;
  }
  return (
    <ul className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-2">
      {data.map((c) => {
        const current = c.id === conversationId;
        return (
          <li key={c.id}>
            <button type="button" aria-current={current ? 'true' : undefined} onClick={() => onPick(c.id)}
              className={cn('flex min-h-11 w-full flex-col items-start rounded-lg px-2 py-1.5 text-left text-sm transition-colors lg:min-h-0',
                current ? 'bg-[hsl(var(--h-primary)/0.1)] text-[hsl(var(--h-primary))]'
                  : 'text-[hsl(var(--h-card-foreground))] hover:bg-[hsl(var(--h-muted))]')}>
              <span className="w-full truncate font-medium">{c.title || 'Hội thoại'}</span>
              <span className="text-xs text-[hsl(var(--h-muted-foreground))]">
                {formatDateTime(c.lastMessageAt ?? c.updatedAt)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Hana's panel (spec §8.1, the workplace mockup's `.panel`): from lg a 380 px column on the right that the page makes
 * room for; below lg a full-screen sheet whose height follows the phone keyboard, so the composer stays in view and
 * only the message list scrolls.
 */
export function AssistantPanel() {
  const appearance = useOwnAppearance();
  const {
    prefs, principal, emotion, still, setOpen, conversationId, setConversationId, switchSeq, hanaUndo, glow, asked, takeAsked,
    openConversation, dropped, takeDrops, ingestedFromMap, shownBatch, takeShown, trayShown,
  } = useAssistant();
  const navigate = useNavigate();
  const page = usePageContext();
  const queryClient = useQueryClient();
  const narrow = useNarrowView();
  const labelId = useId();
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const { pathname, search } = useLocation();
  // The objects of the turn's last step: what an undo offered right after it touches.
  const touched = useRef<AssistantObjectRef[]>([]);
  const turn = useTurn({
    onObjects: (objects, ok) => {
      touched.current = objects;
      // A step that changed something: the page refetches, then the changed cards glow (D88, D128).
      if (ok && objects.length > 0) void refreshFor(queryClient, objects).then(() => glow(objects));
    },
    onUndo: (u) => hanaUndo.offer(u, touched.current),
  });
  const [acting, setActing] = useState<ReadonlySet<Id>>(() => new Set());
  const { act, callOf } = turn;
  // S6 §7.3: an external card stays through its Xác nhận and shows what became of it.
  const ext = useExtConfirm({ conversationId, act, callOf, liveKnown: turn.live?.known,
    liveAsked: (turn.live?.userText ?? '') !== '' });
  const { confirm: confirmExt } = ext;
  const onAct = useCallback((actionId: Id, kind: 'confirm' | 'reject') => {
    const key = conversationId === null ? null : conversationKey(conversationId);
    const conv = key === null ? undefined : queryClient.getQueryData<AssistantConversationDTO>(key);
    const card = conv?.pending.find((p) => p.actionId === actionId);
    // Layer review F1: the card is anchored to the last stored message, so it stays in its turn once settled. Re-review
    // F6: when the last read of the conversation failed, the cache may end at an earlier turn, so the card goes to the
    // bottom instead of under a message that is not its turn's.
    if (kind === 'confirm' && card?.external !== undefined) {
      const fresh = key !== null && queryClient.getQueryState(key)?.status === 'success';
      confirmExt(card, fresh ? conv?.messages.at(-1)?.id ?? null : null);
      return;
    }
    setActing((s) => new Set(s).add(actionId));
    dropPending(queryClient, conversationId, actionId);
    // G3 F1: a reloaded or edited card's call is the one it carries (the live map knows only this stream's cards).
    void act(actionId, kind, callOf(actionId) ?? card?.callId ?? null).finally(() => setActing((s) => {
      const next = new Set(s);
      next.delete(actionId);
      return next;
    }));
  }, [act, callOf, queryClient, conversationId, confirmExt]);
  // What the panel body shows: the conversation, its history, "Briefing trước" (T11.2) or "Lô chờ duyệt" (§8.4).
  const [view, setView] = useState<PanelView>('chat');
  // D192: "Em đã nhớ" is claimed once per panel open, the first time the chat shows.
  const memory = useMemoryNotices(view === 'chat');
  // D150/D151: "Briefing trước" only for a person who receives a briefing; prefs that cannot be read show no button.
  const proactive = useProactivePrefs();
  const briefings = proactive.data !== undefined && receivesBriefings(proactive.data);
  // D112: the ⋯ menu. Below lg it also holds Briefing trước and Cài đặt của tôi: six 44 px buttons leave a 360 px
  // phone's header no room for the persona's name.
  const moreButton = useRef<HTMLButtonElement>(null);
  const pendingCount = usePendingCount();
  const [jump, setJump] = useState<Jump | null>(null);
  const jumpDone = useCallback(() => setJump(null), []);
  const openBatchPage = useCallback((jobId: Id) => {
    if (narrow) setOpen(false);                    // the sheet covers the page below lg
    void navigate(`/assistant/batches/${jobId}`);
  }, [narrow, setOpen, navigate]);
  useJumpToCard(jump, jumpDone, openBatchPage);
  // S8 §9 (D89): the figure and "Cài đặt của tôi" open the person's own assistant page; the sheet covers it below lg.
  // Leaving another page asks first when it holds an unsaved draft (the leave guard sees only links); the page itself
  // is not left, its drafts stay mounted.
  const openMyPage = useCallback(() => {
    if (pathname !== ASSISTANT_ME_PATH && !confirmLeave()) return;
    if (narrow) setOpen(false);
    void navigate(ASSISTANT_ME_PATH);
  }, [pathname, narrow, setOpen, navigate]);
  const openBatch = useCallback((job: IngestJobDTO) => {
    setView('chat');
    if (job.conversationId === null) {
      openBatchPage(job.id);
      return;
    }
    const key = conversationKey(job.conversationId);
    const state = queryClient.getQueryState(key);
    setJump({ jobId: job.id, conversationId: job.conversationId,
      since: Math.max(state?.dataUpdatedAt ?? 0, state?.errorUpdatedAt ?? 0) });
    openConversation(job.conversationId);          // a switch leaves the live turn behind (the switchSeq effect)
    // Read the conversation again: the card may have come after what is cached.
    void queryClient.invalidateQueries({ queryKey: key });
  }, [openBatchPage, queryClient, openConversation]);
  // D160: "Xem trong panel" on the Map's batch bar, taken once (a second mount effect finds nothing left).
  useEffect(() => {
    const job = takeShown();
    if (job !== null) openBatch(job);
  }, [shownBatch, takeShown, openBatch]);
  // S7 D102: a run notice shows the tray, which lives on the chat view, whatever view the panel was on.
  const seenTray = useRef(trayShown);
  useEffect(() => {
    if (seenTray.current === trayShown) return;
    seenTray.current = trayShown;
    setView('chat');
  }, [trayShown]);
  // "(qua Hana)" switched the conversation under an open panel (D169): the live turn belonged to the old one.
  const seenSwitch = useRef(switchSeq);
  const { clear } = turn;
  useEffect(() => {
    if (seenSwitch.current === switchSeq) return;
    seenSwitch.current = switchSeq;
    clear();
    takeAsked();                                   // N1: a command held for the old conversation goes with it
    setView('chat');
  }, [switchSeq, clear, takeAsked]);
  // D208: a ✦ Hana command from a card menu, sent once this panel is there to show its turn. One picked while a turn
  // runs stays with the provider (a newer pick replaces it) and goes when that turn ends (P4 G8 review F3).
  const { send, running } = turn;
  useEffect(() => {
    if (running) return;
    const next = takeAsked();
    if (next === null) return;
    setView('chat');
    if (next.context === undefined) {
      void send(next.text, next.objectRef);
      return;
    }
    // S3 D128: "Hỏi Hana thêm" on the briefing card starts a new conversation, the way a nudge's ask does.
    clear();
    setConversationId(null);
    void send(next.text, undefined, next.context);
  }, [asked, takeAsked, send, running, clear, setConversationId]);
  // P4 G8 review F5: a pick with the panel already open puts the caret here too (the menu closing focused its card).
  const seenAsk = useRef(asked);
  useEffect(() => {
    if (seenAsk.current === asked) return;
    seenAsk.current = asked;
    composerRef.current?.focus();
  }, [asked]);
  // S3 D61: "Hỏi Hana" on a nudge starts a new conversation whose first turn carries the nudge as its context.
  const askNudge = useCallback((text: string, nudgeId: Id) => {
    if (running) return;
    setView('chat');
    clear();
    takeAsked();
    setConversationId(null);
    void send(text, undefined, { kind: 'nudge', id: nudgeId });
  }, [running, clear, takeAsked, setConversationId, send]);
  // A message typed over a list goes to the conversation, so the panel shows it and its answer.
  const sendFromComposer = useCallback((text: string) => {
    setView('chat');
    return send(text);
  }, [send]);
  // D114: files and long pastes go to "Luồng bóc" only where the company allows it and the person may write.
  const ingestUsable = prefs.ingestEnabled && !prefs.readOnly;
  const ingest = useIngestInput();
  const drop = useDropZone(ingestUsable, ingest.pick);
  // D160: files dropped on the Map (a card's drop names it as the anchor), taken once like a drop on the panel itself.
  // The Map offers no drop where ingest is not usable; one that comes anyway (prefs changed under it) is let go.
  // D168: each job a Map drop starts is handed back, so the Map (still on screen) shows that batch.
  const { pick } = ingest;
  useEffect(() => {
    for (const d of takeDrops()) {
      if (ingestUsable) {
        pick(d.files, { ...(d.anchorItemId === undefined ? {} : { anchorItemId: d.anchorItemId }), onCreated: ingestedFromMap });
      }
    }
  }, [dropped, takeDrops, pick, ingestUsable, ingestedFromMap]);
  const close = () => setOpen(false);
  const label = pageStatusLabel(pathname, search);
  const status = turn.running ? 'đang trả lời…' : label !== null ? `đang xem: ${label}` : '';
  // P3 review U-3: a phone's header has room for the page label only; the full line stays in the title.
  const shown = narrow && !turn.running && label !== null ? label : status;

  // An aside has no focus management of its own: opening at lg puts the caret in the composer.
  useEffect(() => {
    if (!narrow) composerRef.current?.focus();
  }, [narrow]);

  const body = (
    <>
      <div className="flex items-center gap-2 px-3 py-2">
        {/* D47: the words say "trợ lý"; the figure (hidden from readers) already wears the persona. */}
        <button type="button" aria-label="Mở trang trợ lý của tôi" title="Mở trang trợ lý của tôi" onClick={openMyPage}
          className="-m-0.5 flex-none rounded-full p-0.5 transition-transform hover:scale-105 focus-visible:outline-2
            focus-visible:outline-offset-2 focus-visible:outline-[hsl(var(--h-primary))] motion-reduce:transition-none">
          <Hana appearance={appearance} emotion={emotion} size={40} still={still} follow />
        </button>
        <div className="min-w-0 flex-1 leading-tight">
          <h2 id={labelId} className="truncate text-sm font-bold text-[hsl(var(--h-card-foreground))]">
            <span className="sr-only">Trợ lý </span><span>{prefs.personaName}</span>
          </h2>
          <p className="truncate text-xs text-[hsl(var(--h-muted-foreground))]" title={status || undefined}>{shown}</p>
        </div>
        {/* Below lg four 44 px buttons (D158, D112: Briefing trước and Cài đặt sit in ⋯ there); from lg 28 px. */}
        <div className="flex flex-none items-center gap-0.5 lg:gap-1">
          <HeadButton label="Lịch sử hội thoại" pressed={view === 'history'}
            onClick={() => setView((v) => (v === 'history' ? 'chat' : 'history'))}>
            <History className="size-4" aria-hidden="true" />
          </HeadButton>
          <HeadButton label="Hội thoại mới" onClick={() => {
            setView('chat');
            turn.clear();
            takeAsked();                             // P4 G8 re-review N1: never the fresh conversation's first turn
            setConversationId(null);
            composerRef.current?.focus();
          }}>
            <SquarePen className="size-4" aria-hidden="true" />
          </HeadButton>
          {briefings && !narrow && (
            <HeadButton label="Briefing trước" pressed={view === 'briefings'}
              onClick={() => setView((v) => (v === 'briefings' ? 'chat' : 'briefings'))}>
              <Newspaper className="size-4" aria-hidden="true" />
            </HeadButton>
          )}
          {!narrow && (
            <HeadButton label="Cài đặt của tôi" onClick={openMyPage}>
              <Settings2 className="size-4" aria-hidden="true" />
            </HeadButton>
          )}
          {/* The count read when the menu opens: a batch that turned ready since the panel opened counts too. */}
          <DropdownMenu onOpenChange={(next) => {
            if (next) void queryClient.invalidateQueries({ queryKey: pendingBatchesKey('ready') });
          }}>
            <DropdownMenuTrigger asChild>
              <button ref={moreButton} type="button" aria-label="Thêm tùy chọn" title="Thêm tùy chọn"
                className={cn(ICON_BUTTON, (view === 'batches' || (narrow && view === 'briefings')) && PRESSED)}>
                <Ellipsis className="size-4" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-52">
              <DropdownMenuItem className={cn(ITEM, view === 'batches' && ITEM_ON)}
                onSelect={() => setView('batches')}>
                <Inbox className="size-4" aria-hidden="true" />
                {pendingCount == null ? 'Lô chờ duyệt' : `Lô chờ duyệt · ${pendingCount}`}
              </DropdownMenuItem>
              {narrow && briefings && (
                <DropdownMenuItem className={cn(ITEM, view === 'briefings' && ITEM_ON)}
                  onSelect={() => setView((v) => (v === 'briefings' ? 'chat' : 'briefings'))}>
                  <Newspaper className="size-4" aria-hidden="true" />
                  Briefing trước
                </DropdownMenuItem>
              )}
              {narrow && (
                <DropdownMenuItem className={ITEM} onSelect={openMyPage}>
                  <Settings2 className="size-4" aria-hidden="true" />
                  Cài đặt của tôi
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <HeadButton label="Đóng" onClick={close}>
            <X className="size-4" aria-hidden="true" />
          </HeadButton>
        </div>
      </div>
      <SelectedLine objectRef={page?.objectRef} />
      {/* D103/D109: the tray and the messages share the height the composer leaves. Inside this region the messages'
          160 px floor is capped at the region less the tray's 1rem padding (all a squeezed tray keeps), so on a short
          sheet (a keyboard, a long draft) the floor gives way and the composer below is never pushed out. */}
      <div data-panel-body="" className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {view === 'chat' && <NudgeTray userId={principal.userId} onAsk={askNudge}
          onEmptied={() => composerRef.current?.focus()} />}
        {view === 'batches' ? (
          <PendingBatches onOpen={openBatch} onClose={() => {
            setView('chat');
            moreButton.current?.focus();
          }} />
        ) : view === 'briefings' ? <BriefingHistory /> : view === 'history' ? (
          <HistoryList onPick={(id) => {
            setView('chat');
            if (id !== conversationId) turn.clear();
            setConversationId(id);
          }} />
        ) : (
          <MessageList live={turn.live} pollUntil={turn.pollUntil} onSend={(text) => { void turn.send(text); }}
            running={turn.running} acting={acting} onAct={onAct} ext={ext.entries}
            remember={<RememberLine memory={memory} />} />
        )}
      </div>
      <Composer composerRef={composerRef} send={sendFromComposer} running={turn.running} refusal={turn.refusal}
        ingest={ingestUsable ? ingest : null} />
      {drop.over && (
        <div aria-hidden="true"
          className="pointer-events-none absolute inset-3 z-10 grid place-items-center rounded-xl border-2 border-dashed border-[hsl(var(--h-primary)/0.4)] bg-[hsl(var(--h-primary)/0.06)] text-sm font-bold text-[hsl(var(--h-primary))] backdrop-blur-[1px]">
          {`Thả file để ${prefs.personaName} đọc`}
        </div>
      )}
    </>
  );

  if (!narrow) {
    /*
     * U-4 (D186): Escape closes the desktop column, the way the phone sheet already closes. Handled on the aside, not
     * the window, so the board's own Escape (deselect) outside the panel is never taken. A layer inside the panel that
     * answered the key first (a Radix picker marks it `defaultPrevented` in its capture listener) keeps the panel open,
     * and so does any open modal: the desktop column is never one itself, so a modal is a dialog above it (its own
     * "Cài đặt" or the page's) and the key is that dialog's. An IME composing a word keeps its Escape. The focus goes
     * back to the opener through the provider's effect.
     */
    const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.nativeEvent.isComposing) return;
      if (document.querySelector(MODAL_OPEN) !== null) return;
      e.preventDefault();
      close();
    };
    return (
      <aside id={PANEL_ID} data-assistant-panel="" data-still={still ? '' : undefined} onKeyDown={onKeyDown}
        {...drop.handlers}
        aria-label={`Trợ lý ${prefs.personaName}`}
        className="fixed inset-y-0 right-0 z-30 flex w-[380px] flex-col border-l border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))]">
        {body}
      </aside>
    );
  }
  return (
    <ModalDialog variant="sheet" labelId={labelId} onClose={close} initialFocus={composerRef}
      className="flex h-[calc(100dvh-var(--kb,0px))] min-h-0 w-full max-w-none flex-col overflow-hidden bg-[hsl(var(--h-card))] p-0">
      <div id={PANEL_ID} data-assistant-panel="" data-still={still ? '' : undefined} {...drop.handlers}
        className="relative flex min-h-0 flex-1 flex-col">
        {body}
      </div>
    </ModalDialog>
  );
}
